import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, FileText, Activity, Download, TrendingUp,
  DollarSign, UserCheck, Clock, AlertCircle, RefreshCw,
  ShieldCheck, KeyRound, Power, Trash2, ExternalLink, Eye
} from 'lucide-react';

const APP_BUILD_DATE = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().slice(0, 10);

// ==================== USER ROW (with management actions) ====================
const UserRow = ({ user, isFounder, onChange }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const userId = user.user_id || user.id;
  const isActive = user.active !== false; // default true if missing

  const planLabel = (p) => ({ starter: 'Starter', solo: 'Solo', clinica: 'Clínica' }[p] || (p || 'starter'));
  const subStatus = user.subscription_status; // active | trialing | past_due | canceled | null

  const stripeCustomerUrl = user.stripe_customer_id
    ? `https://dashboard.stripe.com/customers/${user.stripe_customer_id}`
    : null;

  const handleToggleActive = async () => {
    if (!window.confirm(`${isActive ? 'Desativar' : 'Reativar'} o usuário ${user.email}?`)) return;
    try {
      setBusy(true);
      await api.admin.setUserStatus(userId, !isActive);
      toast.success(isActive ? 'Usuário desativado' : 'Usuário reativado');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`EXCLUIR PERMANENTEMENTE o usuário ${user.email} e TODOS os seus dados? Essa ação não pode ser desfeita.`)) return;
    try {
      setBusy(true);
      await api.admin.deleteUser(userId);
      toast.success('Usuário excluído');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`admin-user-row-${userId}`}>
      <td className="py-3 px-2">
        <div>
          <div className="font-medium">{user.full_name || 'N/A'}</div>
          <div className="text-xs text-gray-500">{user.email}</div>
        </div>
      </td>
      <td className="py-3 px-2">
        <Badge variant={user.plan_type === 'clinica' ? 'default' : user.plan_type === 'solo' ? 'secondary' : 'outline'}>
          {planLabel(user.plan_type)}
        </Badge>
        {subStatus && (
          <div className="text-xs text-gray-500 mt-1">{subStatus}</div>
        )}
      </td>
      <td className="py-3 px-2">
        {!isActive ? (
          <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Desativado</Badge>
        ) : subStatus === 'past_due' ? (
          <Badge variant="outline" className="bg-amber-100 text-amber-800">Pagamento em atraso</Badge>
        ) : subStatus === 'canceled' ? (
          <Badge variant="outline" className="bg-gray-200 text-gray-700">Cancelado</Badge>
        ) : (
          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Ativo</Badge>
        )}
      </td>
      <td className="text-center py-3 px-2">{user.patient_count || 0}</td>
      <td className="text-center py-3 px-2">{user.consultation_count || 0}</td>
      <td className="py-3 px-2 text-gray-500 text-xs">
        {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '—'}
      </td>
      <td className="py-3 px-2">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(`/admin/users/${userId}/consultations`)}
            data-testid={`view-consultations-${userId}`}
            title="Ver consultas do usuário"
          >
            <Eye className="w-4 h-4" />
          </Button>
          {stripeCustomerUrl && (
            <a
              href={stripeCustomerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent"
              title="Abrir cliente no Stripe"
              data-testid={`stripe-link-${userId}`}
            >
              <ExternalLink className="w-4 h-4 text-purple-500" />
            </a>
          )}
          <Button
            variant="ghost" size="sm"
            disabled={busy}
            onClick={handleToggleActive}
            title={isActive ? 'Desativar' : 'Reativar'}
            data-testid={`toggle-active-${userId}`}
          >
            <Power className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-red-500'}`} />
          </Button>
          {isFounder && (
            <Button
              variant="ghost" size="sm"
              disabled={busy}
              onClick={handleDelete}
              title="Excluir usuário"
              data-testid={`delete-user-${userId}`}
              className="hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isFounder = Boolean(authUser?.is_founder);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsData, activityData] = await Promise.all([
        api.admin.getStats(),
        api.admin.getActivity()
      ]);
      
      setStats(statsData);
      setActivity(activityData.activity || []);
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (err.response?.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
      } else {
        setError('Erro ao carregar dados. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      setExporting(type);
      let response;
      let filename;

      switch (type) {
        case 'users':
          response = await api.admin.exportUsers(format);
          filename = `users_export.${format}`;
          break;
        case 'patients':
          response = await api.admin.exportPatients(format);
          filename = `patients_export.${format}`;
          break;
        case 'consultations':
          response = await api.admin.exportConsultations(format);
          filename = `consultations_export.${format}`;
          break;
        case 'all':
          response = await api.admin.exportAll();
          filename = `akali_full_export_${new Date().toISOString().split('T')[0]}.json`;
          break;
        default:
          return;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Erro ao exportar dados');
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2">Carregando dados...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-500 text-lg">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Painel Administrativo</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Visão geral da plataforma Akali AI
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/members')}
              data-testid="admin-dashboard-members-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Administradores
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/change-password')}
              data-testid="admin-dashboard-change-password-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Alterar senha
            </Button>
            <Button 
              variant="outline" 
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button 
              onClick={() => handleExport('all')}
              disabled={exporting === 'all'}
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting === 'all' ? 'Exportando...' : 'Exportar Tudo'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
              <Users className="w-4 h-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.users?.total || 0}</div>
              <p className="text-xs text-green-500">
                +{stats?.users?.new_this_week || 0} esta semana
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Consultas</CardTitle>
              <FileText className="w-4 h-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.consultations?.total || 0}</div>
              <p className="text-xs text-green-500">
                +{stats?.consultations?.new_this_week || 0} esta semana
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Uso da IA</CardTitle>
              <Activity className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.consultations?.ai_usage_rate || 0}%</div>
              <p className="text-xs text-gray-500">
                {stats?.consultations?.with_documentation || 0} docs gerados
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Receita Estimada</CardTitle>
              <DollarSign className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                R$ {(stats?.revenue?.monthly_estimate_brl || 0).toLocaleString('pt-BR')}
              </div>
              <p className="text-xs text-gray-500">
                {stats?.revenue?.paying_users || 0} usuários pagantes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Users by Plan */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Usuários por Plano</CardTitle>
              <CardDescription>Distribuição de usuários entre os planos</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                    <span>Starter (Gratuito)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats?.users?.by_plan?.starter || 0}</span>
                    <Badge variant="secondary">
                      {stats?.users?.total > 0 
                        ? Math.round((stats?.users?.by_plan?.starter / stats?.users?.total) * 100) 
                        : 0}%
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span>Solo (R$ 129/mês)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats?.users?.by_plan?.solo || 0}</span>
                    <Badge variant="default">
                      {stats?.users?.total > 0 
                        ? Math.round((stats?.users?.by_plan?.solo / stats?.users?.total) * 100) 
                        : 0}%
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span>Clínica (R$ 99/assento)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{stats?.users?.by_plan?.clinica || 0}</span>
                    <Badge variant="outline">
                      {stats?.users?.total > 0 
                        ? Math.round((stats?.users?.by_plan?.clinica / stats?.users?.total) * 100) 
                        : 0}%
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Taxa de Onboarding</span>
                  <span className="font-medium">{stats?.users?.onboarding_rate || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Status das Consultas</CardTitle>
              <CardDescription>Distribuição por etapa do fluxo</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>Rascunho</span>
                  </div>
                  <span className="font-bold">{stats?.consultations?.by_status?.draft || 0}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-yellow-500" />
                    <span>Em Revisão</span>
                  </div>
                  <span className="font-bold">{stats?.consultations?.by_status?.in_review || 0}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-500" />
                    <span>Aprovadas</span>
                  </div>
                  <span className="font-bold">{stats?.consultations?.by_status?.approved || 0}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Download className="w-4 h-4 text-blue-500" />
                    <span>Exportadas</span>
                  </div>
                  <span className="font-bold">{stats?.consultations?.by_status?.exported || 0}</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Com Transcrição</span>
                  <span className="font-medium">{stats?.consultations?.with_transcription || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Export Section */}
        <Card>
          <CardHeader>
            <CardTitle>Exportar Dados</CardTitle>
            <CardDescription>Baixe os dados da plataforma em CSV ou JSON</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex flex-col gap-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Usuários</span>
                </div>
                <p className="text-sm text-gray-500">{stats?.users?.total || 0} registros</p>
                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('users', 'csv')}
                    disabled={exporting === 'users'}
                  >
                    CSV
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('users', 'json')}
                    disabled={exporting === 'users'}
                  >
                    JSON
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-green-500" />
                  <span className="font-medium">Pacientes</span>
                </div>
                <p className="text-sm text-gray-500">{stats?.patients?.total || 0} registros</p>
                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('patients', 'csv')}
                    disabled={exporting === 'patients'}
                  >
                    CSV
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('patients', 'json')}
                    disabled={exporting === 'patients'}
                  >
                    JSON
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-500" />
                  <span className="font-medium">Consultas</span>
                </div>
                <p className="text-sm text-gray-500">{stats?.consultations?.total || 0} registros</p>
                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('consultations', 'csv')}
                    disabled={exporting === 'consultations'}
                  >
                    CSV
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExport('consultations', 'json')}
                    disabled={exporting === 'consultations'}
                  >
                    JSON
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2 p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Exportação Completa</span>
                </div>
                <p className="text-sm text-gray-500">Todos os dados</p>
                <Button 
                  size="sm" 
                  onClick={() => handleExport('all')}
                  disabled={exporting === 'all'}
                  className="mt-2"
                >
                  {exporting === 'all' ? 'Exportando...' : 'Baixar JSON'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Activity Table */}
        <Card>
          <CardHeader>
            <CardTitle>Gestão de Usuários</CardTitle>
            <CardDescription>Lista, status, billing e ações por usuário</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Usuário</th>
                    <th className="text-left py-3 px-2">Plano</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-center py-3 px-2">Pacientes</th>
                    <th className="text-center py-3 px-2">Consultas</th>
                    <th className="text-left py-3 px-2">Cadastro</th>
                    <th className="text-right py-3 px-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.slice(0, 50).map((user, index) => (
                    <UserRow
                      key={user.user_id || index}
                      user={user}
                      isFounder={isFounder}
                      onChange={loadData}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {activity.length > 50 && (
              <p className="text-center text-sm text-gray-500 mt-4">
                Mostrando 50 de {activity.length} usuários
              </p>
            )}
          </CardContent>
        </Card>

        {/* System Links */}
        <Card data-testid="admin-system-links">
          <CardHeader>
            <CardTitle>Links e Versão do Sistema</CardTitle>
            <CardDescription>Observabilidade e build atual</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="https://akali-pilates.sentry.io/issues/?project=akali-frontend"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-2 p-4 border rounded-lg hover:border-blue-500 transition-colors"
                data-testid="system-link-sentry"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-purple-500" />
                  <span className="font-medium">Sentry — Erros</span>
                </div>
                <p className="text-xs text-gray-500">Dashboard de erros frontend e backend</p>
                <span className="text-xs text-blue-600 dark:text-blue-400">Abrir →</span>
              </a>

              <a
                href="https://eu.posthog.com/project/default/insights"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-2 p-4 border rounded-lg hover:border-blue-500 transition-colors"
                data-testid="system-link-posthog"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  <span className="font-medium">PostHog — Analytics</span>
                </div>
                <p className="text-xs text-gray-500">Funis, retenção e eventos de produto</p>
                <span className="text-xs text-blue-600 dark:text-blue-400">Abrir →</span>
              </a>

              <div className="flex flex-col gap-2 p-4 border rounded-lg" data-testid="system-link-version">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">Versão</span>
                </div>
                <p className="text-xs text-gray-500">App build atualmente em produção</p>
                <code className="text-xs bg-muted px-2 py-1 rounded">akali-frontend@0.2.0</code>
                <span className="text-xs text-gray-400">
                  Último deploy: {APP_BUILD_DATE}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 pb-8">
          <p>Dados atualizados em: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString('pt-BR') : 'N/A'}</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
